import { Children, Fragment, useId, type ReactElement, type ReactNode } from "react";
import styled from "styled-components";

import { FieldContextProvider } from "./FieldContext";
import { defaultTextVariants, type TextVariantDefinition } from "../Text";
import { isRenderableNode } from "../../utils/reactNode";

export type FieldLayout = "spread" | "stack" | "inline" | "hug";

export type FieldLabelPlacement = "start" | "end";

export interface FieldProps {
  /**
   * Label content rendered as a `<label htmlFor>`. Falsy values render no label. Typography comes
   * from `textVariant`; a string is toned automatically when disabled. A custom node can read
   * `useFieldContext()` to tone itself (the label renders inside the Field context).
   */
  label?: ReactNode;
  /**
   * Label **typography** variant (token) — size / weight / line-height. Defaults to caption. Pass
   * e.g. `defaultTextVariants["body-normal"]` for a checkbox/switch + label. Color is owned by Field
   * (`text.default` / disabled `text.disabled`), so a `color` in the variant style is ignored — for a
   * colored label pass a custom node as `label` (it can read `useFieldContext()`).
   */
  textVariant?: TextVariantDefinition;
  /**
   * `"spread"` puts label/control at row edges; `"stack"` places label above the control;
   * `"inline"` gives the label a fixed width (`labelWidth`) and lets the control fill the rest,
   * so stacked Fields align their control start regardless of label length (e.g. coordinate grids);
   * `"hug"` shrinks to content-width with label/control adjacent — for an inline checkbox/switch
   * next to its label (pair with `labelPlacement`).
   */
  layout?: FieldLayout;
  /**
   * Label position relative to the control. `"start"` (default) puts the label before the control
   * (label left/top — the form-field convention); `"end"` puts it after, for checkbox/radio-style
   * controls where the box precedes the text (control-first). Mainly used with `layout="hug"`.
   */
  labelPlacement?: FieldLabelPlacement;
  /**
   * Fixed label width (CSS length) when `layout="inline"`. Give the same value to several Fields
   * to align their control start. Defaults to `"6rem"`. Ignored outside `inline` layout.
   */
  labelWidth?: string;
  /** Group disabled state propagated to field-aware controls. */
  disabled?: boolean;
  /** Label/control id. Inside Field, this is the single source of truth. */
  id?: string;
  /**
   * A single control element. Fragments are rejected because multiple controls would receive
   * the same generated id.
   */
  children: ReactElement;
}

const Root = styled.div<{ $layout: FieldLayout }>`
  min-width: 0;
  ${({ $layout }) => {
    if ($layout === "hug") {
      // content-width, label/control 인접. spread와 달리 width:100% 아님 — Field가 폭을 소유한다.
      return `
        display: inline-flex;
        align-items: center;
        gap: 0.5em;
      `;
    }
    if ($layout === "stack") {
      return `
        display: flex;
        width: 100%;
        flex-direction: column;
        align-items: stretch;
        gap: 0.4rem;
      `;
    }
    if ($layout === "inline") {
      // Fixed-width label (Label's $width) + control fills the rest; default justify (flex-start).
      return `
        display: flex;
        width: 100%;
        flex-direction: row;
        align-items: center;
        gap: 0.7rem;
      `;
    }
    return `
      display: flex;
      width: 100%;
      justify-content: space-between;
      align-items: center;
      gap: 0.7rem;
    `;
  }}
`;

// Typography comes from `textVariant` (inline style); the structure (color/cursor/width) is here.
const Label = styled.label<{ $disabled: boolean; $width?: string }>`
  ${({ $width }) => ($width ? `width: ${$width};` : "")}
  /* Keep row labels from shrinking before full-width controls. */
  flex-shrink: 0;
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.color.text.disabled : theme.color.text.default};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
`;

/**
 * Composition primitive for one labelable control: label, layout, id wiring, and disabled context.
 */
export function Field({
  label,
  textVariant,
  layout = "spread",
  labelPlacement = "start",
  labelWidth = "6rem",
  disabled = false,
  id,
  children,
}: FieldProps) {
  const generatedId = useId();
  const controlId = id ?? generatedId;

  // Children.only allows a single Fragment, but that can hide multiple controls.
  const child = Children.only(children);
  if (child.type === Fragment) {
    throw new Error(
      "<Field> expects a single control element as its child, not a Fragment. " +
        "Render one control directly, or use multiple <Field>s.",
    );
  }

  const hasLabel = isRenderableNode(label);
  // Composite controls (e.g. RangeSlider) wire this id via aria-labelledby on their group.
  const labelId = hasLabel ? `${controlId}-label` : undefined;
  // textVariant은 typography 전용 — color는 <Label>이 default/disabled로 소유한다. variant.style에 color가
  // 있어도 inline으로 흘리지 않아(styled disabled color를 덮지 않게), disabled 라벨이 항상 dim된다.
  const variantStyle = (textVariant ?? defaultTextVariants["caption-normal"]).style ?? {};
  const labelStyle = { ...variantStyle, color: undefined };

  const labelEl = hasLabel ? (
    <Label
      id={labelId}
      htmlFor={controlId}
      $disabled={disabled}
      $width={layout === "inline" ? labelWidth : undefined}
      style={labelStyle}
    >
      {label}
    </Label>
  ) : null;

  return (
    // Provider wraps the label too (not just the control) so custom label nodes can read
    // useFieldContext() — e.g. to tone themselves when disabled. String labels are toned by <Label>.
    <FieldContextProvider value={{ controlId, labelId, disabled }}>
      <Root $layout={layout}>
        {/* labelPlacement="end" renders the control before the label (control-first); htmlFor keeps a11y order-independent. */}
        {labelPlacement === "end" ? (
          <>
            {child}
            {labelEl}
          </>
        ) : (
          <>
            {labelEl}
            {child}
          </>
        )}
      </Root>
    </FieldContextProvider>
  );
}

Field.displayName = "Field";
